// GestorUsuarios.jsx - Componente principal corregido
import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaUserPlus, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { 
  getUsersPaginados, 
  updateUserWithRole, 
  createUserWithRole,
  getRolesDisponibles 
} from '../../../components/http/Actions/UsuariosAction';

// Componentes
import UserTable from './components/UserTable';
import Pagination from './components/Pagination';
import EditUserModal from './components/EditUserModal';
import CreateUserModal from './components/CreateUserModal';
import Notification from './components/Notification';
import SearchBar from './components/SearchBar';

const GestorUsuarios = () => {
  // Estado para almacenar la lista de usuarios
  const [users, setUsers] = useState([]);
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  // Estado para paginación desde el servidor
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  // Estado para nuevo usuario
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    dateOfBirth: '',
    country: '',
    phoneNumber: '',
    isPremium: false,
    habilitado: true
  });
  // Estado para mensajes de error
  const [error, setError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [updateError, setUpdateError] = useState('');
  // Estado para notificación
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  // Estado para carga
  const [isLoading, setIsLoading] = useState(false);
  // Estado para roles
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState('');
  // Estado para el rol seleccionado en edición/creación
  const [selectedRolId, setSelectedRolId] = useState('');
  // Token de autenticación
  const token = localStorage.getItem("token");

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Esperar 500ms después de que el usuario deje de escribir

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cargar roles al iniciar
  useEffect(() => {
    getRolesDisponibles(setRoles, setRolesError, token)
      .catch(error => {
        console.error("Error loading roles:", error);
        showNotification("Error al cargar los roles", "error");
      });
  }, []);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar usuarios con paginación desde el servidor
  const fetchUsers = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    try {
      await getUsersPaginados(
        (data) => {
          if (data && data.data && Array.isArray(data.data)) {
            setUsers(data.data);
            if (data.pagination) {
              setPagination({
                currentPage: data.pagination.currentPage,
                pageSize: data.pagination.pageSize,
                totalCount: data.pagination.totalCount,
                totalPages: data.pagination.totalPages,
                hasNext: data.pagination.hasNext,
                hasPrevious: data.pagination.hasPrevious
              });
            }
          } else if (Array.isArray(data)) {
            setUsers(data);
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setUsers([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        token
      );
    } catch (error) {
      console.error("Error fetching users:", error);
      showNotification("Error al cargar los usuarios", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage,
      pageSize: 5
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Resetear a la primera página cuando se busca
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      pageSize: 5
    }));
  };

  // Cargar usuarios cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchUsers(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchUsers, pagination.pageSize]);

  // Función para abrir modal de edición
  const openEditModal = async (user) => {
    setUserToEdit({
      ...user,
      dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : ''
    });

    // Buscar el ID del rol basado en el nombre del rol
    try {
      const rolesList = await getRolesDisponibles(setRoles, setRolesError, token);
      const userRole = rolesList.find(r => r.name === user.rol);
      setSelectedRolId(userRole ? userRole.id : '');
    } catch (error) {
      console.error("Error loading roles for edit:", error);
      setSelectedRolId('');
    }
    
    setShowEditModal(true);
  };

  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      dateOfBirth: '',
      country: '',
      phoneNumber: '',
      isPremium: false,
      habilitado: true
    });
    setSelectedRolId(''); // Resetear el rol seleccionado
    setShowCreateModal(true);
  };

  // Función para editar usuario con rol
  const handleEditSubmit = async (e, editedUser, rolId) => {
    e.preventDefault();
    
    if (!rolId) {
      setUpdateError("Debe seleccionar un rol para el usuario");
      return;
    }
    
    setIsLoading(true);
    
    try {
      await updateUserWithRole(editedUser.id, editedUser, rolId, setUpdateError, token);
      
      // Actualizar la lista de usuarios
      fetchUsers(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      
      showNotification(`Usuario ${editedUser.firstName} ${editedUser.lastName} actualizado con éxito`, "success");
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para crear usuario con rol
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRolId) {
      setRegisterError("Debe seleccionar un rol para el usuario");
      return;
    }
    
    setIsLoading(true);
    
    try {
      await createUserWithRole(newUser, selectedRolId, setRegisterError, token);
      
      // Actualizar la lista de usuarios
      fetchUsers(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      
      // Mostrar notificación de éxito
      showNotification(`Usuario ${newUser.firstName} ${newUser.lastName} creado con éxito`, "success");
      
      // Cerrar el modal
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating user:", error);
      // No cerramos el modal en caso de error para permitir corregir
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cambiar el estado de habilitación de un usuario
  const toggleUserStatus = async (user) => {
    try {
      const updatedUser = {
        ...user,
        habilitado: !user.habilitado
      };
      
      // Usar updateUserWithRole en lugar de updateUser
      // Primero, encontrar el ID del rol basado en el nombre
      const userRole = roles.find(r => r.name === user.rol);
      const rolId = userRole ? userRole.id : roles.find(r => r.name === "Basico")?.id || "";
      
      await updateUserWithRole(user.id, updatedUser, rolId, setUpdateError, token);
      
      // Actualizar la lista de usuarios
      fetchUsers(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      
      showNotification(
        `Usuario ${user.firstName} ${user.lastName} ${!user.habilitado ? 'habilitado' : 'deshabilitado'} con éxito`, 
        "success"
      );
    } catch (error) {
      console.error("Error updating user status:", error);
      showNotification("Error al actualizar el estado del usuario", "error");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Usuarios</h1>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <FaUserPlus className="mr-2" />
            Nuevo Usuario
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search and filter */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Users table */}
        {!isLoading && !error && (
          <UserTable 
            users={users} 
            toggleUserStatus={toggleUserStatus} 
            openEditModal={openEditModal} 
          />
        )}

        {/* Paginación desde el backend */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Edit user modal */}
      {showEditModal && userToEdit && (
        <EditUserModal 
          userToEdit={userToEdit}
          setUserToEdit={setUserToEdit}
          handleEditSubmit={(e) => handleEditSubmit(e, userToEdit, selectedRolId)}
          isLoading={isLoading}
          updateError={updateError}
          roles={roles}
          selectedRolId={selectedRolId}
          setSelectedRolId={setSelectedRolId}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Create user modal */}
      {showCreateModal && (
        <CreateUserModal 
          newUser={newUser}
          setNewUser={setNewUser}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          registerError={registerError}
          setRegisterError={setRegisterError}
          roles={roles}
          selectedRolId={selectedRolId}
          setSelectedRolId={setSelectedRolId}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedRolId(''); // Resetear rol seleccionado al cerrar
          }}
          onSubmit={handleCreateSubmit}
        />
      )}
    </div>
  );
};

export default GestorUsuarios;